/**
 * MER-489: pure parsing/summarization over a pipeline_runs row's
 * `stages` jsonb blob, extracted so node:test can pin the "a failed
 * insights stage must never hide inside an overall-completed run"
 * rule without a live Supabase round trip.
 *
 * `stages` shape varies by writer: the spawn sub-object
 * (storage/supabase.py start_pipeline_run) carries {actor, run_id,
 * trigger_source}; the orchestrator's stage writes (pipeline/
 * orchestrator.py, pipeline/run_record.py) carry per-stage
 * {status, error_detail | error, rows_in, rows_out}. Anything else
 * renders with an "unknown" status rather than being dropped, so a
 * future writer's new shape stays visible instead of disappearing.
 */

const FAILED_STATUSES = new Set(["failed", "error", "blocked"]);
const OK_STATUSES = new Set(["ran", "ok", "success", "completed", "skipped", "warn", "partial"]);

/** Normalize a stage's self-reported status string into a fixed vocabulary. */
export function normalizeStageStatus(raw) {
  if (typeof raw !== "string" || raw.length === 0) return "unknown";
  const s = raw.toLowerCase();
  if (FAILED_STATUSES.has(s)) return "failed";
  if (OK_STATUSES.has(s)) return s === "success" || s === "completed" ? "ran" : s;
  return "unknown";
}

/** Per-stage rows from a pipeline_runs.stages blob, "spawn" excluded (see spawnInfo). */
export function stageEntries(stages) {
  if (!stages || typeof stages !== "object") return [];
  return Object.entries(stages)
    .filter(([key]) => key !== "spawn")
    .map(([key, value]) => {
      const v = value && typeof value === "object" ? value : {};
      const error =
        typeof v.error_detail === "string"
          ? v.error_detail
          : typeof v.error === "string"
            ? v.error
            : null;
      return {
        key,
        status: normalizeStageStatus(v.status),
        error,
        rowsIn: typeof v.rows_in === "number" ? v.rows_in : null,
        rowsOut: typeof v.rows_out === "number" ? v.rows_out : null,
      };
    });
}

/** The spawn sub-object: who/what triggered the run (MER-475 attribution). */
export function spawnInfo(stages) {
  const spawn = stages && typeof stages === "object" ? stages.spawn : null;
  if (!spawn || typeof spawn !== "object") {
    return { actor: null, triggerSource: null, apiRunId: null };
  }
  return {
    actor: typeof spawn.actor === "string" ? spawn.actor : null,
    triggerSource: typeof spawn.trigger_source === "string" ? spawn.trigger_source : null,
    apiRunId: typeof spawn.run_id === "string" ? spawn.run_id : null,
  };
}

/**
 * True when any insights-shaped stage key ("insights", or a
 * sub-entity write like "compute:INSIGHT") failed, regardless of the
 * run's overall status. Insight generation is non-blocking, so a run
 * can read `completed` overall while its insights stage failed
 * (MER-489 acceptance: "must show failed insight runs as failed").
 */
export function hasFailedInsightsStage(stages) {
  return stageEntries(stages).some((s) => /insight/i.test(s.key) && s.status === "failed");
}

/** One pipeline_runs row -> the shape the Runs viewer renders. */
export function summarizeRun(row) {
  const stages = row && typeof row === "object" ? row.stages : null;
  const stageList = stageEntries(stages);
  const spawn = spawnInfo(stages);
  return {
    id: row.id,
    customerId: row.customer_id,
    status: typeof row.status === "string" ? row.status : "unknown",
    triggeredBy: row.triggered_by ?? spawn.triggerSource ?? "scheduler",
    actor: spawn.actor,
    startedAt: row.started_at ?? null,
    completedAt: row.completed_at ?? null,
    durationSeconds: typeof row.duration_seconds === "number" ? row.duration_seconds : null,
    error: typeof row.error === "string" ? row.error : null,
    stages: stageList,
    hasFailedInsights: hasFailedInsightsStage(stages),
  };
}

/** "42s" | "3m 8s" | "-" (unknown/negative). */
export function formatDuration(seconds) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return "-";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}
